import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { 
  subscribeToTransferAlerts, 
  unsubscribeFromChannel,
  fetchRecentTransferAlerts 
} from '../../../services/realtimeService';

const LiveTransferAlerts = () => {
  const [transferAlerts, setTransferAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Fetch initial data
    loadTransferAlerts();

    // Subscribe to real-time updates
    const channel = subscribeToTransferAlerts((payload) => {
      handleRealtimeUpdate(payload);
    });

    return () => {
      unsubscribeFromChannel(channel);
    };
  }, []);

  const loadTransferAlerts = async () => {
    setLoading(true);
    try {
      const { data, error: fetchError } = await fetchRecentTransferAlerts(90);
      if (fetchError) {
        setError(fetchError);
      } else {
        setTransferAlerts(data || []);
      }
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRealtimeUpdate = (payload) => {
    console.log('Real-time transfer alert:', payload);
    
    // Reload data when new transfer is added
    if (payload?.eventType === 'INSERT' || payload?.eventType === 'UPDATE') {
      loadTransferAlerts();
    }
  };

  const formatCurrency = (value, currency = 'EUR') => {
    if (!value) return 'Free Transfer';
    const numValue = parseFloat(value);
    if (numValue >= 1000000) {
      return `${currency} ${(numValue / 1000000)?.toFixed(1)}M`;
    }
    if (numValue >= 1000) {
      return `${currency} ${(numValue / 1000)?.toFixed(0)}K`;
    }
    return `${currency} ${numValue?.toFixed(0)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date?.toLocaleDateString();
  };

  const getTransferTypeColor = (type) => {
    switch (type) {
      case 'permanent':
        return 'bg-success/10 text-success border-success/30';
      case 'loan':
        return 'bg-warning/10 text-warning border-warning/30';
      case 'free-transfer':
        return 'bg-primary/10 text-primary border-primary/30';
      case 'end-of-loan':
        return 'bg-muted text-muted-foreground border-border';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getTransferTypeLabel = (type) => {
    switch (type) {
      case 'permanent':
        return 'Permanent';
      case 'loan':
        return 'Loan';
      case 'free-transfer':
        return 'Free';
      case 'end-of-loan':
        return 'End of Loan';
      default:
        return type;
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
            <Icon name="ArrowRightLeft" size={20} color="var(--color-warning)" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Live Transfer Alerts</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-error/10 flex items-center justify-center">
            <Icon name="AlertCircle" size={20} color="var(--color-error)" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Live Transfer Alerts</h3>
        </div>
        <p className="text-sm text-error">{error}</p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
            <Icon name="ArrowRightLeft" size={20} color="var(--color-warning)" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Live Transfer Alerts</h3>
            <p className="text-xs text-muted-foreground">Recent player movements</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
          <span className="text-xs text-success font-medium">LIVE</span>
        </div>
      </div>

      {transferAlerts?.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="ArrowRightLeft" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3 opacity-50" />
          <p className="text-sm text-muted-foreground">No transfer alerts in the last 90 days</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {transferAlerts?.slice(0, 10)?.map((alert) => (
            <div 
              key={alert?.id} 
              className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
            >
              <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 bg-muted">
                {alert?.player?.profilePhoto ? (
                  <Image 
                    src={alert?.player?.profilePhoto} 
                    alt={`${alert?.player?.fullName || 'Player'} profile photo`}
                    className="w-full h-full object-cover" 
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-warning/10">
                    <Icon name="User" size={20} color="var(--color-warning)" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-foreground truncate">
                  {alert?.player?.fullName || alert?.player?.knownAs || 'Unknown Player'}
                </h4>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-muted-foreground truncate">
                    {alert?.fromClub || 'Unknown'}
                  </span>
                  <Icon name="ArrowRight" size={12} color="var(--color-muted-foreground)" />
                  <span className="text-xs text-foreground font-medium truncate">
                    {alert?.toClub || 'Unknown'}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getTransferTypeColor(alert?.transferType)}`}>
                    {getTransferTypeLabel(alert?.transferType)}
                  </span>
                  <span className="text-xs font-medium text-primary">
                    {formatCurrency(alert?.transferFee, alert?.currency)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(alert?.transferDate)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LiveTransferAlerts;